/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import {
  Typography,
  Input,
  Form,
  Select,
  DatePicker,
  TimePicker,
  Accordion,
  Switch,
  Button,
  Checkbox,
  useToast,
} from '@crisellevaldez/speculo-ui';
import { useQueryClient } from '@tanstack/react-query';
import { Calendar, PlusCircle, MapPin, FileText, Check, X, Info } from 'lucide-react';
import { IconPill } from '../../common/IconPill';
import { useOrganizationUsers } from '../../../hooks/useOrganizationUsers';
import { buildTransferLineOptions } from '../../../utils/flows/formatTransferNumbers';
import { FormattedTextarea } from '../../common/FormattedTextarea';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { getMinDate, getMinTime, getMaxTime } from '../../../utils/dateTime';
import { AddTeamMemberModal } from './AddTeamMemberModal';
import { TIMEZONE_OPTIONS } from '../../../constants/timezones';
import { Tooltip } from '../../common/Tooltip';
import { ImproveWithAIButton } from './ImproveWithAIButton';

export const TOTAL_STEPS = 5; // 0-4 (Name, Description, Transfer, Schedule, Summary)

interface FlowDescriptionSelectorProps {
  customDescription: string;
  onCustomDescriptionChange: (value: string) => void;
  descriptionError?: string;
}

const FlowDescriptionSelector: React.FC<FlowDescriptionSelectorProps> = ({
  customDescription,
  onCustomDescriptionChange,
  descriptionError,
}) => {
  return (
    <div className="space-y-4">
      <Form.Control>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
          <Form.Label className="mb-0">Description of the Revive Flow Campaign</Form.Label>
          <Tooltip
            content="Edit the description directly or click 'Improve Description' to enhance your description and make it more professional and clear."
            position="right"
          >
            <span className="group rounded-full transition-colors cursor-help flex items-center">
              <Info className="w-4 h-4 text-gray-400 group-hover:text-yellow-600" />
            </span>
          </Tooltip>
          <ImproveWithAIButton text={customDescription} onImproved={onCustomDescriptionChange} />
        </div>
        <div className="space-y-2">
          <FormattedTextarea
            placeholder="We are calling people who reached out to us in the past about real estate in [insert primary client location like Columbus, Nashville, Tampa, etc.]"
            value={customDescription}
            onChange={(value) => onCustomDescriptionChange(value)}
            required
            error={descriptionError}
            rows={4}
            data-tour="flow-description-input"
          />
          <p className="text-muted-foreground text-sm">
            Be as detailed as possible as the AI will use this as its knowledge to have good
            conversations.
          </p>
        </div>
      </Form.Control>
    </div>
  );
};

interface ReviveFlowStepsProps {
  step: number;
  // Flow basic info
  flowName: string;
  selectedDescription: string;
  customDescription: string;
  transferLines: string[];
  // Schedule settings
  startDate: Date | null;
  preferredTime: string | null;
  schedulingValue: number;
  schedulingUnit: string;
  allowedDays: string[];
  allowedStartTime: string | null;
  allowedEndTime: string | null;
  excludeHolidays: boolean;
  useRoundRobin?: boolean;
  timezone?: string;
  country?: string;
  // Errors
  flowNameError?: string;
  descriptionError?: string;
  startDateError?: string;
  preferredTimeError?: string;
  schedulingError?: string;
  allowedDaysError?: string;
  // Basic info handlers
  onFlowNameChange: (value: string) => void;
  onSelectedDescriptionChange: (value: string) => void;
  onCustomDescriptionChange: (value: string) => void;
  onTransferLinesChange: (value: string[]) => void;
  // Schedule handlers
  onStartDateChange: (value: Date | null) => void;
  onPreferredTimeChange: (value: string | null) => void;
  onSchedulingValueChange: (value: number) => void;
  onSchedulingUnitChange: (value: string) => void;
  onAllowedDaysChange: (value: string[]) => void;
  onAllowedStartTimeChange: (value: string | null) => void;
  onAllowedEndTimeChange: (value: string | null) => void;
  onExcludeHolidaysChange: (value: boolean) => void;
  onUseRoundRobinChange?: (value: boolean) => void;
  onTimezoneChange?: (value: string) => void;
  onCountryChange?: (value: string) => void;
  onInviteModalChange?: (isOpen: boolean) => void;
}

export const ReviveFlowSteps: React.FC<ReviveFlowStepsProps> = ({
  step,
  // Flow basic info
  flowName,
  selectedDescription,
  customDescription,
  transferLines,
  // Schedule settings
  startDate,
  preferredTime,
  schedulingValue,
  schedulingUnit,
  allowedDays,
  allowedStartTime,
  allowedEndTime,
  excludeHolidays,
  useRoundRobin,
  timezone: timezoneProp,
  country: countryProp,
  // Errors
  flowNameError,
  descriptionError,
  startDateError,
  preferredTimeError,
  schedulingError,
  allowedDaysError,
  // Basic info handlers
  onFlowNameChange,
  onSelectedDescriptionChange,
  onCustomDescriptionChange,
  onTransferLinesChange,
  // Schedule handlers
  onStartDateChange,
  onPreferredTimeChange,
  onSchedulingValueChange,
  onSchedulingUnitChange,
  onAllowedDaysChange,
  onAllowedStartTimeChange,
  onAllowedEndTimeChange,
  onExcludeHolidaysChange,
  onUseRoundRobinChange,
  onTimezoneChange,
  onCountryChange,
  onInviteModalChange,
}) => {
  const [showAddTeamMember, setShowAddTeamMember] = useState(false);
  const [isAddingTeamMember, setIsAddingTeamMember] = useState(false);
  const { users, loading: loadingUsers, addTeamMember } = useOrganizationUsers();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { organization } = useOrganizationStore();
  // Use prop timezone if provided, otherwise fall back to org timezone
  const timezone = timezoneProp || organization?.timezone || 'UTC';
  // Use prop country if provided, otherwise default to US
  const country = countryProp || 'US';

  // Set default values
  useEffect(() => {
    // Default days Mon-Sat for all sources
    if (!allowedDays.length) {
      onAllowedDaysChange(['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']);
    }

    // Default hours 9am-6pm for all sources
    if (!allowedStartTime) {
      onAllowedStartTimeChange('09:00');
    }
    if (!allowedEndTime) {
      onAllowedEndTimeChange('18:00');
    }

    // Default scheduling window
    if (!schedulingValue) {
      onSchedulingValueChange(365);
      onSchedulingUnitChange('days');
      onExcludeHolidaysChange(true);
    }
  }, []);

  // Update time restrictions when date changes
  useEffect(() => {
    if (startDate) {
      const minTime = getMinTime(startDate, timezone);
      // Reset time if:
      // 1. No valid times available for today (minTime is null)
      // 2. No time is currently selected
      // 3. Selected time is before the minimum allowed time for today
      if (minTime === null) {
        onPreferredTimeChange(null);
      } else if (!preferredTime || preferredTime < minTime) {
        onPreferredTimeChange(minTime);
      }
    }
  }, [startDate, timezone, preferredTime, onPreferredTimeChange]);

  // Handle scheduling unit changes
  useEffect(() => {
    if (schedulingUnit === 'hours' && schedulingValue !== 1) {
      onSchedulingValueChange(1);
    }
  }, [schedulingUnit]);

  const steps = [
    // Step 0: Flow Name
    <div key="step0" className="space-y-6" data-tour="flow-step-0">
      <div className="space-y-4">
        <Form.Control>
          <Form.Label>What is the name of this Revive Flow?</Form.Label>
          <Input
            className="min-h-[40px]"
            placeholder="Enter Flow name"
            value={flowName}
            onChange={(e) => onFlowNameChange(e.target.value)}
            required
            error={flowNameError}
            helperText="This is the internal name. Make sure it is something that is unique to this flow."
            data-tour="flow-name-input"
          />
        </Form.Control>
      </div>
    </div>,

    // Step 1: Flow Description
    <div key="step1" className="space-y-6" data-tour="flow-step-1">
      <div className="space-y-4">
        <FlowDescriptionSelector
          customDescription={customDescription}
          onCustomDescriptionChange={onCustomDescriptionChange}
          descriptionError={descriptionError}
        />
      </div>
    </div>,

    // Step 2: Transfer Line
    <div key="step2" className="space-y-2" data-tour="flow-step-2">
      <div className="space-y-4">
        <Form.Control>
          <Form.Label>Transfer Line(s)</Form.Label>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1 min-w-0" data-tour="transfer-line-select">
              <Select
                className="min-h-9"
                isLoading={loadingUsers}
                options={buildTransferLineOptions(users)}
                rows={4}
                value={transferLines}
                onChange={(value) => {
                  if (Array.isArray(value)) {
                    onTransferLinesChange(value);
                    // Reset useRoundRobin to false if less than 2 transfer lines
                    if (value.length < 2 && useRoundRobin && onUseRoundRobinChange) {
                      onUseRoundRobinChange(false);
                    }
                  }
                }}
                placeholder="Select team members"
                multiple={true}
                searchable
              />
            </div>
            <Button
              variant="secondary"
              className="whitespace-nowrap h-9 w-full sm:w-auto justify-center"
              onClick={() => setShowAddTeamMember(true)}
              data-tour="add-team-member-button"
            >
              <PlusCircle className="w-4 h-4 mr-2" />
              Add New Team
            </Button>
          </div>
        </Form.Control>

        {/* Use Round Robin Checkbox */}
        {transferLines.length > 1 && (
          <Form.Control className="flex items-center">
            <div className="flex mt-2 gap-2">
              <Checkbox
                id="useRoundRobin"
                checked={useRoundRobin || false}
                onChange={(e) => onUseRoundRobinChange && onUseRoundRobinChange(e.target.checked)}
                label="Enable Round Robin for this Flow"
              />
            </div>
          </Form.Control>
        )}
      </div>
      <AddTeamMemberModal
        isOpen={showAddTeamMember}
        onClose={() => {
          setShowAddTeamMember(false);
          onInviteModalChange?.(false);
        }}
        onSubmit={async (data) => {
          setIsAddingTeamMember(true);
          try {
            if (!organization?.id) throw new Error('No organization ID');
            await addTeamMember(organization.id, data);
            await queryClient.invalidateQueries({ queryKey: ['organizationUsers'] });
            setShowAddTeamMember(false);
            showToast({ message: 'Team member invited successfully', variant: 'success' });
            if (data.phoneNumber) onTransferLinesChange([...transferLines, data.phoneNumber]);
          } catch (error) {
            showToast({
              message: error instanceof Error ? error.message : 'Error inviting team member',
              variant: 'error',
            });
            throw error;
          } finally {
            setIsAddingTeamMember(false);
          }
        }}
        isSubmitting={isAddingTeamMember}
      />
    </div>,

    // Step 3: Schedule Settings
    <div key="step3" className="space-y-8" data-tour="flow-step-3">
      <div className="space-y-6">
        {/* Timezone Selection - At Top */}
        <Form.Control>
          <Form.Label>Timezone</Form.Label>
          <Select
            placeholder="Select timezone"
            value={timezone}
            onChange={(value) => {
              const newTimezone = value as string;
              if (onTimezoneChange) {
                onTimezoneChange(newTimezone);
              }
              // Auto-set country based on timezone
              if (onCountryChange) {
                if (newTimezone.includes('Canada') || newTimezone.startsWith('Canada/')) {
                  onCountryChange('CA');
                } else {
                  onCountryChange('US');
                }
              }
            }}
            options={TIMEZONE_OPTIONS}
            searchable
            helperText="Select the timezone for scheduling this flow."
          />
        </Form.Control>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-tour="schedule-datetime">
          <Form.Control>
            <Form.Label>Schedule Your Flow</Form.Label>
            <DatePicker
              placeholder="Select start date"
              value={startDate}
              onChange={onStartDateChange}
              minDate={getMinDate(timezone)}
              error={startDateError}
            />
          </Form.Control>
          <Form.Control>
            <Form.Label>Preferred Time</Form.Label>
            <TimePicker
              placeholder="Select preferred time"
              value={preferredTime}
              onChange={(value) => {
                // Only update if the selected time is valid
                const minTime = startDate ? getMinTime(startDate, timezone) : '08:00';
                if (value && minTime && value >= minTime) {
                  onPreferredTimeChange(value);
                }
              }}
              minTime={startDate ? getMinTime(startDate, timezone) || undefined : '08:00'}
              maxTime={getMaxTime(timezone)}
              step={60} // 60 minutes = 1 hour increments
              use24Hour={false} // Use 12-hour format for display
              error={preferredTimeError}
              disabled={startDate ? getMinTime(startDate, timezone) === null : false} // Disable if no valid times available
            />
          </Form.Control>
        </div>

        <div data-tour="advanced-settings-accordion">
          <Accordion
            type="single"
            className="border-0 shadow-none bg-transparent"
            items={[
              {
                title: 'Advanced Settings',
                children: (
                  <div className="space-y-6">
                    {/* Scheduling Window */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Form.Control>
                        <Form.Label>Scheduling Window</Form.Label>
                        <Input
                          className="min-h-[40px]"
                          placeholder="Enter value"
                          type="number"
                          value={schedulingValue}
                          onChange={(e) => onSchedulingValueChange(Number(e.target.value))}
                          helperText="Set the range for when the AI can schedule calls."
                          error={schedulingError}
                        />
                      </Form.Control>
                      <Form.Control>
                        <Form.Label>&nbsp;</Form.Label>
                        <Select
                          placeholder="Select unit"
                          value={schedulingUnit}
                          onChange={(value) => onSchedulingUnitChange(value as string)}
                          options={[
                            { value: 'days', label: 'Days' },
                            { value: 'hours', label: 'Hours' },
                          ]}
                        />
                      </Form.Control>
                    </div>

                    {/* Allowed Days */}
                    <Form.Control>
                      <Form.Label>Allowed Days</Form.Label>
                      <Select
                        placeholder="Select days"
                        multiple={true}
                        value={allowedDays}
                        onChange={(value) => {
                          if (Array.isArray(value)) {
                            onAllowedDaysChange(value);
                          }
                        }}
                        options={[
                          { value: 'Monday', label: 'Monday' },
                          { value: 'Tuesday', label: 'Tuesday' },
                          { value: 'Wednesday', label: 'Wednesday' },
                          { value: 'Thursday', label: 'Thursday' },
                          { value: 'Friday', label: 'Friday' },
                          { value: 'Saturday', label: 'Saturday' },
                          { value: 'Sunday', label: 'Sunday' },
                        ]}
                        helperText="Choose the days of the week when calls can be scheduled."
                        error={allowedDaysError}
                      />
                    </Form.Control>

                    {/* Allowed Hours */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Form.Control>
                        <Form.Label>Start Time</Form.Label>
                        <TimePicker
                          placeholder="Select start time"
                          value={allowedStartTime}
                          onChange={onAllowedStartTimeChange}
                          minTime="08:00"
                          maxTime="20:00"
                          step={60}
                          helperText="Set time range for when calls can occur."
                        />
                      </Form.Control>
                      <Form.Control>
                        <Form.Label>End Time</Form.Label>
                        <TimePicker
                          placeholder="Select end time"
                          value={allowedEndTime}
                          onChange={onAllowedEndTimeChange}
                          minTime="08:00"
                          maxTime="20:00"
                          step={60}
                        />
                      </Form.Control>
                    </div>

                    {/* Country and Exclude Holidays */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Form.Control>
                        <Form.Label>Country</Form.Label>
                        <Select
                          placeholder="Select country"
                          value={country}
                          onChange={(value) => onCountryChange && onCountryChange(value as string)}
                          options={[
                            { value: 'US', label: 'United States' },
                            { value: 'CA', label: 'Canada' },
                          ]}
                          helperText="Select the country for holiday exclusions."
                        />
                      </Form.Control>
                      <Form.Control className="flex items-center">
                        <div className="flex mt-5 gap-2">
                          <Form.Label className="mb-0">Exclude Holidays</Form.Label>
                          <Switch
                            size="sm"
                            checked={excludeHolidays}
                            onChange={(e) => onExcludeHolidaysChange(e.target.checked)}
                          />
                        </div>
                      </Form.Control>
                    </div>
                  </div>
                ),
              },
            ]}
          />
        </div>
      </div>
    </div>,

    // Step 4: Summary
    <div key="step4" className="space-y-8" data-tour="flow-step-4">
      <div className="bg-white dark:bg-transparent rounded-lg">
        {/* Flow Summary Header and Info - highlighted during tour */}
        <div data-tour="flow-summary-info">
          <div className="flex flex-col justify-center items-center gap-2 mb-5">
            <Typography variant="h5" className="text-xl font-bold text-gray-900 dark:text-gray-100">
              Flow Summary
            </Typography>
            <p className="text-red-600 text-sm">
              Please review your flow settings below. Click "Previous" if you need to make any
              changes.
            </p>
          </div>

          {/* Flow Information Card */}
          <div className="bg-gray-50 dark:bg-white/5 p-6 rounded-lg border border-gray-300 dark:border-white/10">
            <div className="flex items-center gap-4 mb-6">
              <IconPill icon={FileText} size="lg" />
              <Typography
                variant="h6"
                className="text-base font-semibold text-gray-900 dark:text-gray-100"
              >
                Flow Information
              </Typography>
            </div>
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <div className="text-sm font-medium text-gray-800 dark:text-gray-300">
                    Flow Name
                  </div>
                  <div className="text-sm mt-2 text-gray-700 dark:text-gray-400">{flowName}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-800 dark:text-gray-300">
                    Transfer Line(s)
                  </div>
                  <div className="text-sm mt-2 text-gray-700 dark:text-gray-400">
                    {transferLines.length > 0
                      ? buildTransferLineOptions(users)
                          .filter((opt) => transferLines.includes(opt.value))
                          .map((opt) => opt.label)
                          .join(', ')
                      : 'None selected'}
                  </div>
                </div>
              </div>
              {transferLines.length > 1 && (
                <div>
                  <div className="text-sm font-medium text-gray-800 dark:text-gray-300">
                    Round Robin
                  </div>
                  <div className="mt-2">
                    <span
                      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        useRoundRobin
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          : 'bg-gray-200 text-gray-800 dark:bg-white/10 dark:text-gray-300'
                      }`}
                    >
                      {useRoundRobin ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                      {useRoundRobin ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>
              )}
              <div>
                <div className="text-sm font-medium text-gray-800 dark:text-gray-300">
                  Description
                </div>
                <div className="mt-2 text-sm text-gray-700 dark:text-gray-400 whitespace-pre-wrap rounded border border-gray-100 dark:border-white/10">
                  {customDescription || 'No description provided'}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6 mt-6">
          {/* Schedule Settings Card */}
          <div className="bg-gray-50 dark:bg-white/5 p-6 rounded-lg border border-gray-300 dark:border-white/10">
            <div className="flex items-center gap-4 mb-6">
              <IconPill icon={Calendar} size="lg" />
              <Typography
                variant="h6"
                className="text-base font-semibold text-gray-900 dark:text-gray-100"
              >
                Schedule Settings
              </Typography>
            </div>
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <div className="text-sm font-medium text-gray-800 dark:text-gray-300">
                    Scheduled Date/Time
                  </div>
                  <div className="mt-2 text-gray-700 dark:text-gray-400 text-sm">
                    {startDate
                      ? `${startDate.toLocaleDateString()} ${preferredTime || ''} ${timezone}`
                      : 'Not scheduled'}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-800 dark:text-gray-300">
                    Scheduling Window
                  </div>
                  <div className="text-sm mt-2 text-gray-700 dark:text-gray-400">
                    {schedulingValue && schedulingUnit
                      ? `${schedulingValue} ${schedulingUnit}`
                      : 'Not set'}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <div className="text-sm font-medium text-gray-800 dark:text-gray-300">
                    Allowed Hours
                  </div>
                  <div className="text-sm mt-2 text-gray-700 dark:text-gray-400">
                    {allowedStartTime && allowedEndTime
                      ? `${allowedStartTime} - ${allowedEndTime}`
                      : 'Not set'}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-800 dark:text-gray-300">
                    Allowed Days
                  </div>
                  <div className="mt-2">
                    <div className="flex flex-wrap gap-2">
                      {allowedDays.map((day) => (
                        <span
                          key={day}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                        >
                          {day}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Location Settings Card */}
          <div className="bg-gray-50 dark:bg-white/5 p-6 rounded-lg border border-gray-300 dark:border-white/10">
            <div className="flex items-center gap-4 mb-6">
              <IconPill icon={MapPin} size="lg" />
              <Typography
                variant="h6"
                className="text-base font-semibold text-gray-900 dark:text-gray-100"
              >
                Location Settings
              </Typography>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <div className="text-sm font-medium text-gray-800 dark:text-gray-300">Timezone</div>
                <div className="text-sm mt-2 text-gray-700 dark:text-gray-400">{timezone}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-800 dark:text-gray-300">Country</div>
                <div className="text-sm mt-2 text-gray-700 dark:text-gray-400">
                  {country === 'CA' ? 'Canada' : 'United States'}
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-800 dark:text-gray-300">
                  Exclude Holidays
                </div>
                <div className="mt-2">
                  <span
                    className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      excludeHolidays
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                        : 'bg-gray-200 text-gray-800 dark:bg-white/10 dark:text-gray-300'
                    }`}
                  >
                    {excludeHolidays ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                    {excludeHolidays ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
  ];

  return <div className="py-6">{steps[step] || null}</div>;
};
